import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useInfiniteList } from '@/lib/pagination/use-infinite-list';
import { createCaseFromFlight, getFlight, listFlights } from './api';
import type {
  FlightMonitorFilters,
  ICreateCaseResult,
  IFlightMonitorRow,
  IFlightsMonitorPage,
} from './types';

/** How far around "now" the universal search looks for flights (±24h — the
 *  overnight tail the passenger search covers plus the upcoming day). */
const FLIGHT_HITS_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Tenant-wide FLIGHT lookup for the universal dashboard search — one monitor
 * page (`GET /v1/flights?q=`) over a ±24h window, NOT the bare schedule picker:
 * monitor rows carry the live case/disruption marks, which is exactly the
 * search's "this flight is part of a disruption → jump to it" affordance.
 * Enabled from 2 characters; previous hits linger while typing (placeholderData),
 * mirroring `usePnrSearch`.
 */
export function useFlightHits(q: string) {
  const needle = q.trim();
  return useQuery<IFlightsMonitorPage>({
    queryKey: ['flights', 'hit-search', needle],
    queryFn: () => {
      const now = Date.now();
      return listFlights({
        q: needle,
        from: new Date(now - FLIGHT_HITS_WINDOW_MS).toISOString(),
        to: new Date(now + FLIGHT_HITS_WINDOW_MS).toISOString(),
      });
    },
    enabled: needle.length >= 2,
    staleTime: 10_000,
    placeholderData: keepPreviousData,
  });
}

/** ONE flight by URN (the Express board header). Degrades open BFF-side, so a
 *  resolved row may carry no `status` — render what's there. */
export function useFlight(flightUrn: string | undefined) {
  return useQuery<IFlightMonitorRow>({
    queryKey: ['flights', 'detail', flightUrn],
    queryFn: () => getFlight(flightUrn!),
    enabled: !!flightUrn,
    staleTime: 30_000,
  });
}

/**
 * Flights MONITOR list (NEX-342 / F4) — cursor-paginated + infinite-scrolled
 * (frontend-conventions §7.5.1). Filters are the query-key tail, so a filter
 * change starts a fresh walk.
 *
 * Live via WebSocket (NEX-343 / F5): the /flights route joins the BFF `flights`
 * room on mount and the central applyEvent merge writes each
 * `flight.status-changed` delta straight into THIS cache (`['flights','monitor']`)
 * via setQueryData. The M1 30s react-query poll is REMOVED — real-time UI pushes
 * over the WS, never polls (frontend-conventions; TDS §14 anti-pattern 2).
 */
export function useFlightsMonitor(filters: FlightMonitorFilters) {
  const list = useInfiniteList<IFlightMonitorRow, FlightMonitorFilters>({
    baseKey: ['flights', 'monitor'],
    filters,
    queryFn: (f, cursor) => listFlights(f, cursor),
    staleTime: 30_000,
  });
  // The board summary rides the monitor envelope additively (server fold over
  // the WHOLE filtered board, status filter excluded) — every page carries the
  // same fold, so the first page's is the board's.
  const summary = (list.firstPage as IFlightsMonitorPage | undefined)?.summary;
  return { ...list, summary };
}

/**
 * One-click create-case-from-flight (NEX-342 F4 §6). On success the caller
 * navigates to the (new or pre-existing) case; we invalidate the monitor walk so
 * the row picks up its new case mark.
 */
export function useCreateCaseFromFlight() {
  const qc = useQueryClient();
  return useMutation<ICreateCaseResult, Error, string>({
    mutationFn: (flightUrn) => createCaseFromFlight(flightUrn),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['flights', 'monitor'] });
    },
  });
}
